import React, { useEffect, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import SurveyApiService from "../../services/SurveyApiService";
import { SURVEY_API_ENDPOINT, SCALE_OPTIONS } from "../../constants";
import Url from "app/shared/constants/Url";
import "../../styles/PublicSurvey.scss";

const extractAgeRanges = (scaleObj) => {
  if (!scaleObj) return [];
  if (Array.isArray(scaleObj)) return scaleObj;
  if (Array.isArray(scaleObj.ranges)) return scaleObj.ranges;
  if (Array.isArray(scaleObj.age_ranges)) return scaleObj.age_ranges;
  if (Array.isArray(scaleObj.ageRanges)) return scaleObj.ageRanges;
  if (Array.isArray(scaleObj.data)) return extractAgeRanges(scaleObj.data);
  if (Array.isArray(scaleObj.data?.ranges)) return scaleObj.data.ranges;
  if (Array.isArray(scaleObj.data?.age_ranges)) return scaleObj.data.age_ranges;
  if (Array.isArray(scaleObj.data?.ageRanges)) return scaleObj.data.ageRanges;
  if (Array.isArray(scaleObj.results)) return scaleObj.results;
  if (Array.isArray(scaleObj.result)) return scaleObj.result;
  return [];
};

const DEFAULT_LANGUAGES = [
  { code: "hi", name: "Hindi" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "kn", name: "Kannada" },
  { code: "ml", name: "Malayalam" },
  { code: "gu", name: "Gujarati" },
  { code: "mr", name: "Marathi" },
  { code: "pa", name: "Punjabi" },
  { code: "ur", name: "Urdu" },
  { code: "bn", name: "Bengali" },
  { code: "or", name: "Odia" },
  { code: "as", name: "Assamese" },
  { code: "mni", name: "Manipuri" },
  { code: "kok", name: "Konkani" },
  { code: "sa", name: "Sanskrit" },
  { code: "sat", name: "Santali" },
  { code: "doi", name: "Dogri" },
  { code: "ks", name: "Kashmiri" },
  { code: "mai", name: "Maithili" },
  { code: "sd", name: "Sindhi" },
  { code: "brx", name: "Bodo" },
  { code: "ne", name: "Nepali" },
];

const STATIC_LABELS = {
  basicDetails: "Basic Details",
  basicDetailsSubtitle: "Please provide your details before starting the survey",
  plant: "Plant",
  selectPlant: "Select Plant",
  gender: "Gender",
  selectGender: "Select Gender",
  ageRange: "Age Range",
  age: "Age",
  selectAgeRange: "Select Age Range",
  enterYourAge: "Enter your age",
  designation: "Designation",
  selectDesignation: "Select Designation",
  enterYourDesignation: "Enter your designation",
  department: "Department",
  selectDepartment: "Select Department",
  educationQualification: "Education Qualification",
  selectEducationQualification: "Select Education Qualification",
  mandatory: "Mandatory",
  errorBanner: "You have missed one or more mandatory fields or questions. Please review highlighted items below.",
  fillRequiredDetails: "Please fill in all required basic details.",
  answerMandatoryQuestions: "Please answer all mandatory questions.",
  submitSurvey: "Submit Survey",
  submitting: "Submitting...",
  loadingSurvey: "Loading Survey...",
  error: "Error",
  defaultSurveyTitle: "Survey",
  submittedSuccessfully: "Survey submitted successfully!",
  submissionFailed: "Submission failed. Please try again.",
  failedToSubmit: "Failed to submit response.",
  failedToLoad: "Failed to load the survey.",
  language: "Language",
  translating: "Translating...",
};

const ERROR_MESSAGES = {
  plant: "Plant selection is required",
  ageRangeRequired: "Age range is required",
  ageRequired: "Age is required",
  invalidAge: "Please enter a valid age",
  gender: "Gender selection is required",
  designation: "Designation is required",
  department: "Department selection is required",
  education: "Education qualification selection is required",
};

export default function PublicSurvey() {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const categoryId = searchParams.get("category_id");
  const subCategoryId = searchParams.get("sub_category_id");
  const subSubCategoryId = searchParams.get("sub_sub_category_id");
  const formId = searchParams.get("form_id");

  const [surveyData, setSurveyData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showErrorBanner, setShowErrorBanner] = useState(false);

  // Tracks whether this browser has already submitted this exact survey
  // (same id + category/sub-category/form query params). Persisted in
  // localStorage so it survives a page refresh — public surveys have no
  // login to key an "already answered" check off of otherwise.
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);

  const getSubmittedStorageKey = () =>
    `public_survey_submitted_${id || "unknown"}_${categoryId || ""}_${subCategoryId || ""}_${subSubCategoryId || ""}_${formId || ""}`;

  const [language, setLanguage] = useState("en");
  const [availableLanguages, setAvailableLanguages] = useState(DEFAULT_LANGUAGES);

  const [translationMap, setTranslationMap] = useState({});
  const [translating, setTranslating] = useState(false);

  // Cache of scale-point-count -> option labels, fetched from
  // SURVEY_API_ENDPOINT.surveyQuestionScaleOptions(scale). Falls back to the
  // hardcoded 5-point SCALE_OPTIONS whenever a question's configured scale
  // hasn't been fetched (or the question has no scale override at all).
  const [scaleOptionsCache, setScaleOptionsCache] = useState({});

  // Dynamic Survey Settings State (Plants, Gender, Age Scale, Designation, Department, Education)
  const [settings, setSettings] = useState({
    plants: [],
    genderSetting: { is_male: true, is_female: true, is_others: false },
    ageScale: null,
    designations: [],
    departments: [],
    educations: [],
  });

  // Basic Details Form State
  const [plant, setPlant] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [designation, setDesignation] = useState("");
  const [department, setDepartment] = useState("");
  const [education, setEducation] = useState("");
  const [detailsErrors, setDetailsErrors] = useState({});

  const clearFieldError = (fieldName) => {
    if (detailsErrors[fieldName]) {
      setDetailsErrors((prev) => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };

  const handlePlantChange = (e) => {
    setPlant(e.target.value);
    clearFieldError("plant");
  };

  const handleAgeChange = (e) => {
    setAge(e.target.value);
    clearFieldError("age");
  };

  const handleGenderChange = (e) => {
    setGender(e.target.value);
    clearFieldError("gender");
  };

  const handleDesignationChange = (e) => {
    setDesignation(e.target.value);
    clearFieldError("designation");
  };

  const handleDepartmentChange = (e) => {
    setDepartment(e.target.value);
    clearFieldError("department");
  };

  const handleEducationChange = (e) => {
    setEducation(e.target.value);
    clearFieldError("education");
  };

  const getNormalizedParts = (data) => {
    if (!data) return [];

    const getQuestionsList = (src) => {
      if (!src) return [];
      if (Array.isArray(src)) return src;
      return (
        Array.isArray(src.questions) ? src.questions :
        Array.isArray(src.items) ? src.items :
        Array.isArray(src.uploaded_questions) ? src.uploaded_questions :
        Array.isArray(src.survey_questions) ? src.survey_questions :
        Array.isArray(src.uploaded_ques) ? src.uploaded_ques :
        Array.isArray(src.all_questions) ? src.all_questions :
        Array.isArray(src.ques) ? src.ques :
        Array.isArray(src.data) ? src.data :
        Array.isArray(src.data?.questions) ? src.data.questions :
        []
      );
    };

    if (Array.isArray(data)) {
      return [{ id: "part-1", name: "Survey Questions", sections: [{ id: "sec-1", title: "", questions: data }] }];
    }

    const topQuestions = [
      ...getQuestionsList(data),
      ...(Array.isArray(data.questions) ? data.questions : []),
      ...(Array.isArray(data.all_questions) ? data.all_questions : []),
      ...(Array.isArray(data.survey_questions) ? data.survey_questions : []),
      ...(Array.isArray(data.uploaded_questions) ? data.uploaded_questions : []),
      ...(Array.isArray(data.uploaded_ques) ? data.uploaded_ques : []),
      ...(Array.isArray(data.items) ? data.items : []),
      ...(Array.isArray(data.data?.questions) ? data.data.questions : []),
      ...(Array.isArray(data.data?.survey_questions) ? data.data.survey_questions : []),
    ];

    const matchQuestionToSection = (q, section) => {
      if (!q || !section) return false;
      const qSecId = q.section_id || q.sectionId;
      const qSecTitle = (q.section_title || q.sectionTitle || q.section_name || q.sectionName || "").trim().toLowerCase();
      const secId = String(section.id || section.section_id || section._id || "");
      const secTitle = String(section.title || section.name || section.section_name || "").trim().toLowerCase();

      if (qSecId != null && qSecId !== "" && secId) {
        if (String(qSecId) === secId) return true;
      }
      if (qSecTitle && secTitle) {
        if (qSecTitle === secTitle) return true;
      }
      if (qSecId != null && qSecId !== "" && secTitle) {
        if (String(qSecId).trim().toLowerCase() === secTitle) return true;
      }
      return false;
    };

    const rawParts = Array.isArray(data.parts)
      ? data.parts
      : Array.isArray(data?.data?.parts)
      ? data.data.parts
      : Array.isArray(data?.survey_parts)
      ? data.survey_parts
      : null;

    if (Array.isArray(rawParts) && rawParts.length > 0) {
      return rawParts.map((part, pIdx) => {
        const partId = part.id || part.part_id || part.partId || (pIdx + 1);
        const partName = part.name || part.part_name || `Part ${pIdx + 1}`;

        const rawSections = Array.isArray(part.sections)
          ? part.sections
          : Array.isArray(part.data?.sections)
          ? part.data.sections
          : [];

        const partQuestions = [
          ...getQuestionsList(part),
          ...(Array.isArray(part.questions) ? part.questions : []),
          ...(Array.isArray(part.items) ? part.items : []),
          ...(Array.isArray(part.survey_questions) ? part.survey_questions : []),
          ...topQuestions.filter((q) => {
            const qPartId = q.part_id || q.partId;
            const qPartName = (q.part_name || q.partName || q.part || "").trim().toLowerCase();
            if (qPartId != null && qPartId !== "" && String(qPartId) === String(partId)) return true;
            if (qPartName && partName.trim().toLowerCase() === qPartName) return true;
            return false;
          }),
        ];

        const seenQuestionKeys = new Set();

        const sections = rawSections.map((sec, sIdx) => {
          const secDirectQuestions = getQuestionsList(sec);
          const matchedFromPart = partQuestions.filter((q) => matchQuestionToSection(q, sec));
          const matchedFromTop = topQuestions.filter((q) => matchQuestionToSection(q, sec));

          const allSecQ = [...secDirectQuestions, ...matchedFromPart, ...matchedFromTop];
          const uniqueSecQ = [];

          allSecQ.forEach((q) => {
            if (!q) return;
            const qKey = String(q.id || q.question_id || q._id || q.question_text || q.text);
            if (!seenQuestionKeys.has(qKey)) {
              seenQuestionKeys.add(qKey);
              uniqueSecQ.push(q);
            }
          });

          return {
            ...sec,
            id: sec.id || sec.section_id || sec._id || `sec-${pIdx}-${sIdx}`,
            title: sec.title || sec.name || sec.section_name || `Section ${sIdx + 1}`,
            description: sec.description || "",
            questions: uniqueSecQ,
          };
        });

        // Unassigned questions for this part
        const unassignedQuestions = partQuestions.filter((q) => {
          if (!q) return false;
          const qKey = String(q.id || q.question_id || q._id || q.question_text || q.text);
          return !seenQuestionKeys.has(qKey);
        });

        if (unassignedQuestions.length > 0) {
          const uniqueUnassigned = [];
          unassignedQuestions.forEach((q) => {
            const qKey = String(q.id || q.question_id || q._id || q.question_text || q.text);
            if (!seenQuestionKeys.has(qKey)) {
              seenQuestionKeys.add(qKey);
              uniqueUnassigned.push(q);
            }
          });

          if (uniqueUnassigned.length > 0) {
            if (sections.length === 0) {
              sections.push({
                id: `part-${partId}-general`,
                title: "",
                description: "",
                questions: uniqueUnassigned,
              });
            } else {
              sections[0].questions = [...sections[0].questions, ...uniqueUnassigned];
            }
          }
        }

        return {
          ...part,
          id: partId,
          name: partName,
          sections,
        };
      });
    }

    const rawSections = Array.isArray(data.sections) ? data.sections : [];
    if (rawSections.length > 0) {
      const seenQ = new Set();
      const sections = rawSections.map((sec, sIdx) => {
        const secDirectQuestions = getQuestionsList(sec);
        const matchedFromTop = topQuestions.filter((q) => matchQuestionToSection(q, sec));
        const allQ = [...secDirectQuestions, ...matchedFromTop];
        const uniqueQ = [];
        allQ.forEach((q) => {
          if (!q) return;
          const qKey = String(q.id || q.question_id || q._id || q.question_text || q.text);
          if (!seenQ.has(qKey)) {
            seenQ.add(qKey);
            uniqueQ.push(q);
          }
        });
        return {
          ...sec,
          id: sec.id || sec.section_id || sec._id || `sec-fb-${sIdx}`,
          title: sec.title || sec.name || sec.section_name || `Section ${sIdx + 1}`,
          questions: uniqueQ,
        };
      });
      return [{ id: "part-1", name: "Survey Questions", sections }];
    }

    if (topQuestions.length > 0) {
      return [{ id: "part-1", name: "Survey Questions", sections: [{ id: "sec-1", title: "", questions: topQuestions }] }];
    }

    return [];
  };

  // Which scale (number of points, e.g. 3 or 5) a given question is configured
  // to use. Adjust/extend this field list if your question payload uses a
  // different key.
  const getQuestionScale = (q) => {
    const raw =
      q?.scale ??
      q?.scale_type ??
      q?.scaleType ??
      q?.no_of_scale ??
      q?.rating_scale ??
      q?.number_of_options ??
      q?.no_of_options ??
      null;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  // Resolve the option labels a question should render: use the options
  // embedded directly on the question (the API returns these alongside
  // "scale", e.g. scale: 3 -> options: ["Disagree","Neutral","Agree"]).
  // Fall back to a fetched scale-options-cache entry, then to the default
  // 5-point SCALE_OPTIONS only if neither is available.
  const getOptionsForQuestion = (q) => {
    if (Array.isArray(q?.options) && q.options.length > 0) {
      return q.options.map((o) => (typeof o === "string" ? o : o?.label || o?.name || o?.option || o?.value)).filter(Boolean);
    }
    const scale = getQuestionScale(q);
    if (scale && Array.isArray(scaleOptionsCache[scale]) && scaleOptionsCache[scale].length > 0) {
      return scaleOptionsCache[scale];
    }
    return SCALE_OPTIONS;
  };

  // Translate helper: returns the translated string for the current
  // `language` if we have it cached, otherwise the original English text.
  const tr = (text) => {
    if (!text || language === "en") return text;
    const map = translationMap[language];
    if (map && map.has(text)) return map.get(text);
    return text;
  };

  useEffect(() => {
    let cancelled = false;
    axios
      .get(SURVEY_API_ENDPOINT.translateLanguages)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.languages;
        if (Array.isArray(list) && list.length > 0) {
          setAvailableLanguages(list);
        }
      })
      .catch(() => {
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const collectTranslatableStrings = () => {
    const strings = new Set();

    Object.values(STATIC_LABELS).forEach((s) => s && strings.add(s));
    Object.values(ERROR_MESSAGES).forEach((s) => s && strings.add(s));

    if (surveyData) {
      const title = surveyData?.survey_name || surveyData?.name;
      if (title) strings.add(title);
      const desc = surveyData?.instruction_cover || surveyData?.instructionCover || surveyData?.description;
      if (desc) strings.add(desc);

      getNormalizedParts(surveyData).forEach((part) => {
        if (part.name) strings.add(part.name);
        (part.sections || []).forEach((section) => {
          if (section.title) strings.add(section.title);
          if (section.description) strings.add(section.description);
          (section.questions || []).forEach((q) => {
            const qText = q.question_text || q.text || q.title || q.name || q.question;
            if (qText) strings.add(qText);
            const qDesc = q.description || q.desc;
            if (qDesc) strings.add(qDesc);
            getOptionsForQuestion(q).forEach((opt) => opt && strings.add(opt));
          });
        });
      });
    }

    (settings.plants || []).forEach((p) => {
      const name = p.plant_name || p.name;
      if (name) strings.add(name);
    });
    (settings.designations || []).forEach((d) => d.name && strings.add(d.name));
    (settings.departments || []).forEach((dep) => dep.name && strings.add(dep.name));
    (settings.educations || []).forEach((ed) => ed.name && strings.add(ed.name));
    (settings.ageScale?.ranges || []).forEach((r) => {
      const val = r.label || r.preview || (r.from_age != null ? `${r.from_age} - ${r.to_age}` : null);
      if (val) strings.add(val);
    });
    genderOptions.forEach((g) => g && strings.add(g));

    return Array.from(strings);
  };

  useEffect(() => {
    if (language === "en") return;
    if (translationMap[language]) return;
    if (!surveyData) return;

    let cancelled = false;
    const strings = collectTranslatableStrings();
    if (strings.length === 0) return;

    setTranslating(true);
    axios
      .post(SURVEY_API_ENDPOINT.translateBatch, { texts: strings, to: language })
      .then((res) => {
        if (cancelled) return;
        const translations = res?.data?.translations;
        if (Array.isArray(translations) && translations.length === strings.length) {
          const map = new Map();
          strings.forEach((s, idx) => map.set(s, translations[idx] || s));
          setTranslationMap((prev) => ({ ...prev, [language]: map }));
        } else {
          throw new Error("Malformed translation response");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Translation failed:", err);
        toast.error("Translation failed. Showing English instead.");
        setLanguage("en");
      })
      .finally(() => {
        if (!cancelled) setTranslating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [language, surveyData, settings]);

  useEffect(() => {
    if (!id) return;
    try {
      const submittedFlag = localStorage.getItem(getSubmittedStorageKey());
      if (submittedFlag === "true") {
        setAlreadySubmitted(true);
        toast.error("You have already filled the survey.");
      }
    } catch (e) {
      // localStorage may be unavailable (private browsing, etc.) — fail
      // open and let the person attempt the submission normally.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, categoryId, subCategoryId, subSubCategoryId, formId]);

  useEffect(() => {
    if (!id) return;
    const fetchSurveyData = async () => {
      setLoading(true);
      setError(null);
      let currentAgeScale = null;
      try {
        const queryParams = {};
        if (categoryId) queryParams.category_id = categoryId;
        if (subCategoryId) queryParams.sub_category_id = subCategoryId;
        if (subSubCategoryId) queryParams.sub_sub_category_id = subSubCategoryId;
        if (formId) queryParams.form_id = formId;

        let res;
        try {
          res = await axios.get(SURVEY_API_ENDPOINT.publicForm(id), { params: queryParams });
        } catch (catErr) {
          res = await axios.get(SURVEY_API_ENDPOINT.publicForm(id));
        }

        if (res?.data?.status === 200 || res?.status === 200 || res?.data?.success || res?.success) {
          let data = res?.data?.data || res?.data || res;

          // If publicForm returned parts/sections but no questions attached inside them, fetch questions via API
          const initialParts = getNormalizedParts(data);
          const initialQCount = initialParts.reduce(
            (sum, p) => sum + (p.sections || []).reduce((sSum, sec) => sSum + (sec.questions?.length || 0), 0),
            0
          );

          if (initialQCount === 0) {
            try {
              const qRes = await SurveyApiService.fetchQuestions(null, null, { survey_id: id, form_id: id, ...queryParams });
              const qData = qRes?.data || qRes;
              const rawQList = Array.isArray(qRes)
                ? qRes
                : Array.isArray(qData)
                ? qData
                : Array.isArray(qData?.questions)
                ? qData.questions
                : Array.isArray(qData?.all_questions)
                ? qData.all_questions
                : Array.isArray(qData?.uploaded_questions)
                ? qData.uploaded_questions
                : Array.isArray(qData?.survey_questions)
                ? qData.survey_questions
                : [];
              if (rawQList.length > 0) {
                if (typeof data === "object" && !Array.isArray(data)) {
                  data = { ...data, questions: rawQList };
                }
              }
            } catch (qErr) {}
          }

          setSurveyData(data);

          const sObj = data?.settings || data?.demographics || data;
          currentAgeScale =
            sObj?.age_scale ||
            sObj?.ageScale ||
            sObj?.age_ranges ||
            sObj?.ageRanges ||
            data?.age_scale ||
            data?.ageScale ||
            data?.age_ranges ||
            data?.ageRanges ||
            data?.settings?.age_scale ||
            data?.settings?.ageScale ||
            data?.demographics?.age_scale ||
            data?.demographics?.ageScale ||
            null;

          const extracted = {
            plants: Array.isArray(sObj?.plants)
              ? sObj.plants.filter((p) => p.is_active !== false && p.is_deleted !== 1)
              : Array.isArray(data?.plants)
              ? data.plants.filter((p) => p.is_active !== false && p.is_deleted !== 1)
              : [],
            genderSetting:
              sObj?.gender_setting ||
              sObj?.genderSetting ||
              data?.gender_setting ||
              data?.genderSetting ||
              { is_male: true, is_female: true, is_others: false },
            ageScale: currentAgeScale,
            designations: Array.isArray(sObj?.designations)
              ? sObj.designations.filter((d) => d.is_active !== false && d.is_deleted !== 1)
              : Array.isArray(data?.designations)
              ? data.designations.filter((d) => d.is_active !== false && d.is_deleted !== 1)
              : [],
            departments: Array.isArray(sObj?.departments)
              ? sObj.departments.filter((dep) => dep.is_active !== false && dep.is_deleted !== 1)
              : Array.isArray(data?.departments)
              ? data.departments.filter((dep) => dep.is_active !== false && dep.is_deleted !== 1)
              : [],
            educations: Array.isArray(sObj?.educations)
              ? sObj.educations.filter((ed) => ed.is_active !== false && ed.is_deleted !== 1)
              : Array.isArray(data?.educations)
              ? data.educations.filter((ed) => ed.is_active !== false && ed.is_deleted !== 1)
              : [],
          };
          setSettings(extracted);
        } else {
          setError(res?.data?.msg || `${STATIC_LABELS.failedToLoad} Please try again.`);
        }

        try {
          const settingsRes = await SurveyApiService.fetchFullPageSettings(id, queryParams);
          const sData = settingsRes?.data || settingsRes || {};
          if (sData && (sData.plants || sData.designations || sData.departments || sData.educations || sData.genderSetting || sData.gender_setting || sData.ageScale || sData.age_scale || sData.ageRanges || sData.age_ranges)) {
            const fetchedSecAge = sData.ageScale || sData.age_scale || sData.ageRanges || sData.age_ranges;
            if (fetchedSecAge && extractAgeRanges(fetchedSecAge).length > 0) {
              currentAgeScale = fetchedSecAge;
            }
            setSettings((prev) => ({
              plants: Array.isArray(sData.plants) && sData.plants.length > 0 ? sData.plants.filter((p) => p.is_active !== false) : prev.plants,
              genderSetting: sData.genderSetting || sData.gender_setting || sData.genderSettings || sData.gender_settings || prev.genderSetting,
              ageScale: fetchedSecAge || prev.ageScale,
              designations: Array.isArray(sData.designations) && sData.designations.length > 0 ? sData.designations : prev.designations,
              departments: Array.isArray(sData.departments) && sData.departments.length > 0 ? sData.departments.filter((d) => d.is_active !== false) : prev.departments,
              educations: Array.isArray(sData.educations) && sData.educations.length > 0 ? sData.educations : prev.educations,
            }));
          }
        } catch (sErr) {}

        try {
          const gRes = await SurveyApiService.fetchGenderSettings(id);
          const gData = gRes?.data || gRes;
          if (gData && typeof gData === "object" && !Array.isArray(gData)) {
            setSettings((prev) => ({
              ...prev,
              genderSetting: {
                ...(prev.genderSetting || {}),
                ...gData,
              },
            }));
          }
        } catch (gErr) {}

        // Only attempt secondary API calls for age scale if the primary API response lacked age ranges
        const hasPrimaryAgeRanges = extractAgeRanges(currentAgeScale).length > 0;
        if (!hasPrimaryAgeRanges) {
          let fetchedAgeScale = null;
          const storedUserId = localStorage.getItem("userId");
          const storedAuthKey = localStorage.getItem("springToken") || localStorage.getItem("token");

          const authParams = {};
          if (storedUserId) authParams.user_id = storedUserId;
          if (storedAuthKey) authParams.auth_key = storedAuthKey;

          const directAgeEndpoints = [
            { url: `${Url.nodeClientURL}/survey-settings/age-scale`, params: { survey_id: id, ...authParams } },
            { url: `${Url.nodeClientURL}/survey-settings/age-scale`, params: { survey_id: id } },
            { url: `${Url.nodeClientURL}/survey-settings/age-scale/${id}`, params: authParams },
            { url: `${Url.nodeClientURL}/survey-settings/age-scale/${id}` },
          ];

          for (const ep of directAgeEndpoints) {
            try {
              const ageRes = await axios.get(ep.url, { params: ep.params });
              const aData = ageRes?.data?.data || ageRes?.data || ageRes;
              if (aData) {
                const ranges = extractAgeRanges(aData);
                if (ranges.length > 0) {
                  fetchedAgeScale = aData;
                  break;
                }
              }
            } catch (e) {}
          }

          if (fetchedAgeScale) {
            setSettings((prev) => ({
              ...prev,
              ageScale: fetchedAgeScale,
            }));
          } else {
            try {
              const keysToTry = [
                `survey_setting_age_ranges_${id}`,
                `survey_setting_age_scale_${id}`,
                "demographic_master_age",
              ];
              for (const key of keysToTry) {
                const saved = localStorage.getItem(key);
                if (saved) {
                  const parsed = JSON.parse(saved);
                  const ranges = extractAgeRanges(parsed);
                  if (ranges.length > 0) {
                    setSettings((prev) => ({
                      ...prev,
                      ageScale: prev.ageScale || parsed,
                    }));
                    break;
                  }
                }
              }
            } catch (e) {}
          }
        }
      } catch (err) {
        console.error("Error fetching public survey data:", err);
        setError(STATIC_LABELS.failedToLoad);
      } finally {
        setLoading(false);
      }
    };
    fetchSurveyData();
  }, [id]);

  // Once the survey/questions are loaded, find every distinct scale
  // (e.g. 3-point, 5-point) actually used and fetch its option labels.
  useEffect(() => {
    if (!surveyData) return;

    const scalesNeeded = new Set();
    getNormalizedParts(surveyData).forEach((part) =>
      (part.sections || []).forEach((section) =>
        (section.questions || []).forEach((q) => {
          // Skip questions that already carry their own options array —
          // nothing to fetch for those.
          if (Array.isArray(q?.options) && q.options.length > 0) return;
          const scale = getQuestionScale(q);
          if (scale) scalesNeeded.add(scale);
        })
      )
    );

    scalesNeeded.forEach((scale) => {
      if (scaleOptionsCache[scale]) return; // already fetched / fetching

      axios
        .get(SURVEY_API_ENDPOINT.surveyQuestionScaleOptions(scale))
        .then((res) => {
          const raw =
            res?.data?.data?.options ||
            res?.data?.options ||
            res?.data?.data ||
            res?.data ||
            [];
          const list = Array.isArray(raw) ? raw : [];
          const labels = list
            .map((o) => (typeof o === "string" ? o : o?.label || o?.name || o?.option || o?.value))
            .filter(Boolean);

          if (labels.length > 0) {
            setScaleOptionsCache((prev) => ({ ...prev, [scale]: labels }));
          }
        })
        .catch(() => {
          // Leave this scale unresolved; getOptionsForQuestion() will fall
          // back to the default SCALE_OPTIONS for its questions.
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData]);

  const handleSelectOption = (questionId, option) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: option,
    }));
    if (validationErrors[questionId]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };

  // Derive dynamic choices from settings
  // Only count plants that have a usable name/id - filters out empty/blank
  // entries so the Plant field doesn't render an effectively-empty box.
  const plantList = (settings.plants || []).filter(
    (p) => p && String(p.plant_name || p.name || "").trim() !== ""
  );

  const gSet = settings.genderSetting || {};
  const isValueTrue = (val) => {
    if (val === true || val === 1) return true;
    if (typeof val === "string") {
      const lower = val.trim().toLowerCase();
      return lower === "true" || lower === "1" || lower === "yes";
    }
    return false;
  };

  const isValueFalse = (val) => {
    if (val === false || val === 0) return true;
    if (typeof val === "string") {
      const lower = val.trim().toLowerCase();
      return lower === "false" || lower === "0" || lower === "no";
    }
    return false;
  };

  const maleVal = gSet.is_male ?? gSet.isMale ?? gSet.male;
  const showMale = maleVal === undefined ? true : !isValueFalse(maleVal);

  const femaleVal = gSet.is_female ?? gSet.isFemale ?? gSet.female;
  const showFemale = femaleVal === undefined ? true : !isValueFalse(femaleVal);

  const otherVal =
    gSet.is_others ??
    gSet.is_other ??
    gSet.isOthers ??
    gSet.isOther ??
    gSet.others ??
    gSet.other ??
    gSet.show_other ??
    gSet.showOther ??
    gSet.enable_others ??
    gSet.enableOthers;

  const showOther = isValueTrue(otherVal);

  const genderOptions = [];
  if (showMale) genderOptions.push("Male");
  if (showFemale) genderOptions.push("Female");
  if (showOther) genderOptions.push("Other");
  const ageRangesList = extractAgeRanges(settings.ageScale);
  const designationList = settings.designations || [];
  const departmentList = settings.departments || [];
  const educationList = settings.educations || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (alreadySubmitted) {
      toast.error("You have already filled the survey.");
      return;
    }

    // Perform validation on basic details
    const dErrors = {};
    if (plantList.length > 0 && !plant) {
      dErrors.plant = tr(ERROR_MESSAGES.plant);
    }

    if (!age) {
      dErrors.age = tr(ageRangesList.length > 0 ? ERROR_MESSAGES.ageRangeRequired : ERROR_MESSAGES.ageRequired);
    } else if (ageRangesList.length === 0 && (isNaN(age) || Number(age) <= 0 || Number(age) > 120)) {
      dErrors.age = tr(ERROR_MESSAGES.invalidAge);
    }

    if (!gender) {
      dErrors.gender = tr(ERROR_MESSAGES.gender);
    }

    if (!designation || !designation.trim()) {
      dErrors.designation = tr(ERROR_MESSAGES.designation);
    }

    if (departmentList.length > 0 && !department) {
      dErrors.department = tr(ERROR_MESSAGES.department);
    }

    if (educationList.length > 0 && !education) {
      dErrors.education = tr(ERROR_MESSAGES.education);
    }

    const hasDetailsErrors = Object.keys(dErrors).length > 0;
    setDetailsErrors(dErrors);

    // Perform validation on mandatory questions
    const errors = {};
    let hasErrors = false;

    const normalizedParts = getNormalizedParts(surveyData);
    if (normalizedParts.length > 0) {
      normalizedParts.forEach((part) => {
        if (part.sections) {
          part.sections.forEach((section) => {
            if (section.questions) {
              section.questions.forEach((q) => {
                const isMandatory = q.is_mandatory === 1 || q.isMandatory || q.mandatory;
                if (isMandatory && !answers[q.id]) {
                  errors[q.id] = true;
                  hasErrors = true;
                }
              });
            }
          });
        }
      });
    }

    if (hasDetailsErrors || hasErrors) {
      setValidationErrors(errors);
      setShowErrorBanner(true);

      if (hasDetailsErrors) {
        toast.error(tr(STATIC_LABELS.fillRequiredDetails));
        const element = document.getElementById("ps-details-card");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else {
        toast.error(tr(STATIC_LABELS.answerMandatoryQuestions));
        const firstErrorId = Object.keys(errors)[0];
        const element = document.getElementById(`q-card-${firstErrorId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    setSubmitting(true);
    setShowErrorBanner(false);

    try {
      // Build a quick id -> question lookup so we can score each answer
      // against the option set that question actually rendered (its
      // configured scale, if any, rather than always assuming 5 points).
      const questionsById = {};
      normalizedParts.forEach((part) =>
        (part.sections || []).forEach((section) =>
          (section.questions || []).forEach((q) => {
            questionsById[q.id] = q;
          })
        )
      );

      const payload = Object.entries(answers).map(([qId, val]) => {
        const q = questionsById[qId] || questionsById[Number(qId)];
        const opts = q ? getOptionsForQuestion(q) : SCALE_OPTIONS;
        const idx = opts.indexOf(val);
        const score = idx >= 0 ? opts.length - idx : 0;
        return {
          question_id: Number(qId),
          answer_value: val,
          answer: val,
          input_answer: val,
          score: score >= 1 && score <= opts.length ? score : 0,
        };
      });

      const responsePayload = {
        answers: payload,
        age: ageRangesList.length > 0 ? age : Number(age),
        age_range: ageRangesList.length > 0 ? age : undefined,
        gender,
        designation,
        plant,
        department,
        education,
        category_id: categoryId ? Number(categoryId) : null,
        sub_category_id: subCategoryId ? Number(subCategoryId) : null,
        sub_sub_category_id: subSubCategoryId ? Number(subSubCategoryId) : null,
        form_id: formId ? Number(formId) : null,
      };

      const res = await SurveyApiService.submitPublicSurveyResponse(id, responsePayload);

      if (res?.status === 200 || res?.data?.status === 200 || res?.success || res?.data?.success) {
        try {
          localStorage.setItem(getSubmittedStorageKey(), "true");
        } catch (e) {
          // Ignore storage errors — the submission itself still succeeded.
        }
        setAlreadySubmitted(true);
        toast.success(tr(STATIC_LABELS.submittedSuccessfully));
        history.push(language !== "en" ? `/thankyou/externalpage?lang=${language}` : "/thankyou/externalpage");
      } else {
        toast.error(res?.message || res?.data?.message || res?.msg || res?.data?.msg || tr(STATIC_LABELS.submissionFailed));
      }
    } catch (err) {
      console.error("Error submitting survey response:", err);
      toast.error(err.response?.data?.message || err.response?.data?.msg || err.message || tr(STATIC_LABELS.failedToSubmit));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="ps-page">
        <div className="ps-loading-container">
          <div className="ps-spinner" />
          <p>{STATIC_LABELS.loadingSurvey}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ps-page">
        <div className="ps-container">
          <div className="ps-header-card">
            <h1>{STATIC_LABELS.error}</h1>
            <p className="ps-description">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div className="ps-page">
        <div className="ps-container">
          <div className="ps-header-card">
            <h1>Survey Already Submitted</h1>
            <p className="ps-description">
              You have already filled the survey. Thank you for your response!
            </p>
          </div>
        </div>
      </div>
    );
  }

  const surveyTitle = surveyData?.survey_name || surveyData?.name || STATIC_LABELS.defaultSurveyTitle;
  const surveyDesc = surveyData?.instruction_cover || surveyData?.instructionCover || surveyData?.description || "";
  const coverImage = surveyData?.cover_image_url || surveyData?.coverImageUrl;

  return (
    <div className="ps-page">
      <form onSubmit={handleSubmit} className="ps-container">
        <div className="ps-language-bar">
          <label htmlFor="ps-language-select" className="ps-language-label">
            {tr(STATIC_LABELS.language)}
          </label>
          <select
            id="ps-language-select"
            className="ps-language-select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="en">English</option>
            {availableLanguages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
          {translating && <span className="ps-language-status">{STATIC_LABELS.translating}</span>}
        </div>

        <div className="ps-header-card">
          {coverImage && (
            <img src={coverImage} alt="Survey Cover" className="ps-cover-image" />
          )}
          <h1>{tr(surveyTitle)}</h1>
          {surveyDesc && <p className="ps-description">{tr(surveyDesc)}</p>}
        </div>

        {showErrorBanner && (
          <div className="ps-error-banner">
            <span>⚠️</span>
            <span>{tr(STATIC_LABELS.errorBanner)}</span>
          </div>
        )}

        <div id="ps-details-card" className="ps-details-card">
          <h2>{tr(STATIC_LABELS.basicDetails)}</h2>
          <p className="ps-details-subtitle">{tr(STATIC_LABELS.basicDetailsSubtitle)}</p>

          <div className="ps-details-grid">
            {/* 1. Plant Field (if configured) */}
            {plantList.length > 0 && (
              <div className={`ps-form-group ${detailsErrors.plant ? "ps-form-group--error" : ""}`}>
                <label htmlFor="details-plant">
                  {tr(STATIC_LABELS.plant)}<span className="ps-mandatory-star">*</span>
                </label>
                <select id="details-plant" value={plant} onChange={handlePlantChange}>
                  <option value="">{tr(STATIC_LABELS.selectPlant)}</option>
                  {plantList.map((p) => (
                    <option key={p.id} value={p.plant_name || p.name}>
                      {tr(p.plant_name || p.name)}
                    </option>
                  ))}
                </select>
                {detailsErrors.plant && <span className="ps-field-error-text">{detailsErrors.plant}</span>}
              </div>
            )}

            {/* 2. Gender Field (configured choices) */}
            <div className={`ps-form-group ${detailsErrors.gender ? "ps-form-group--error" : ""}`}>
              <label htmlFor="details-gender">
                {tr(STATIC_LABELS.gender)}<span className="ps-mandatory-star">*</span>
              </label>
              <select id="details-gender" value={gender} onChange={handleGenderChange}>
                <option value="">{tr(STATIC_LABELS.selectGender)}</option>
                {genderOptions.map((gOpt) => (
                  <option key={gOpt} value={gOpt}>
                    {tr(gOpt)}
                  </option>
                ))}
              </select>
              {detailsErrors.gender && <span className="ps-field-error-text">{detailsErrors.gender}</span>}
            </div>

            {/* 3. Age Field (Age Range dropdown vs input) */}
            <div className={`ps-form-group ${detailsErrors.age ? "ps-form-group--error" : ""}`}>
              <label htmlFor="details-age">
                {ageRangesList.length > 0 ? tr(STATIC_LABELS.ageRange) : tr(STATIC_LABELS.age)}
                <span className="ps-mandatory-star">*</span>
              </label>
              {ageRangesList.length > 0 ? (
                <select id="details-age" value={age} onChange={handleAgeChange}>
                  <option value="">{tr(STATIC_LABELS.selectAgeRange)}</option>
                  {ageRangesList.map((r, idx) => {
                    const val =
                      r.label ||
                      r.preview ||
                      (r.from_age !== undefined && r.from_age !== null && r.to_age !== undefined && r.to_age !== null
                        ? `${r.from_age} - ${r.to_age}`
                        : r.from_age !== undefined && r.from_age !== null
                        ? `${r.from_age}+`
                        : r.fromAge !== undefined && r.toAge !== undefined
                        ? `${r.fromAge} - ${r.toAge}`
                        : `Option ${idx + 1}`);
                    return (
                      <option key={r.id || r.sr_no || r.sr || idx} value={val}>
                        {tr(val)}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <input
                  id="details-age"
                  type="number"
                  placeholder={tr(STATIC_LABELS.enterYourAge)}
                  value={age}
                  onChange={handleAgeChange}
                  min="1"
                  max="120"
                />
              )}
              {detailsErrors.age && <span className="ps-field-error-text">{detailsErrors.age}</span>}
            </div>

            {/* 4. Designation Field (configured list vs text input) */}
            <div className={`ps-form-group ${detailsErrors.designation ? "ps-form-group--error" : ""}`}>
              <label htmlFor="details-designation">
                {tr(STATIC_LABELS.designation)}<span className="ps-mandatory-star">*</span>
              </label>
              {designationList.length > 0 ? (
                <select id="details-designation" value={designation} onChange={handleDesignationChange}>
                  <option value="">{tr(STATIC_LABELS.selectDesignation)}</option>
                  {designationList.map((d) => (
                    <option key={d.id} value={d.name}>
                      {tr(d.name)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="details-designation"
                  type="text"
                  placeholder={tr(STATIC_LABELS.enterYourDesignation)}
                  value={designation}
                  onChange={handleDesignationChange}
                />
              )}
              {detailsErrors.designation && <span className="ps-field-error-text">{detailsErrors.designation}</span>}
            </div>

            {/* 5. Department Field (if configured) */}
            {departmentList.length > 0 && (
              <div className={`ps-form-group ${detailsErrors.department ? "ps-form-group--error" : ""}`}>
                <label htmlFor="details-department">
                  {tr(STATIC_LABELS.department)}<span className="ps-mandatory-star">*</span>
                </label>
                <select id="details-department" value={department} onChange={handleDepartmentChange}>
                  <option value="">{tr(STATIC_LABELS.selectDepartment)}</option>
                  {departmentList.map((dep) => (
                    <option key={dep.id} value={dep.name}>
                      {tr(dep.name)}
                    </option>
                  ))}
                </select>
                {detailsErrors.department && <span className="ps-field-error-text">{detailsErrors.department}</span>}
              </div>
            )}

            {/* 6. Education Qualification Field (if configured) */}
            {educationList.length > 0 && (
              <div className={`ps-form-group ${detailsErrors.education ? "ps-form-group--error" : ""}`}>
                <label htmlFor="details-education">
                  {tr(STATIC_LABELS.educationQualification)}<span className="ps-mandatory-star">*</span>
                </label>
                <select id="details-education" value={education} onChange={handleEducationChange}>
                  <option value="">{tr(STATIC_LABELS.selectEducationQualification)}</option>
                  {educationList.map((ed) => (
                    <option key={ed.id} value={ed.name}>
                      {tr(ed.name)}
                    </option>
                  ))}
                </select>
                {detailsErrors.education && <span className="ps-field-error-text">{detailsErrors.education}</span>}
              </div>
            )}
          </div>
        </div>

        {getNormalizedParts(surveyData).map((part, partIdx) => (
            <div key={part.id || partIdx} className="ps-part-section">
              <h2 className="ps-part-title">{tr(part.name)}</h2>
              {part.sections &&
                part.sections.map((section, secIdx) => (
                  <div key={section.id || secIdx} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {(section.title || section.description) && (
                      <div className="ps-section-card">
                        {section.title && <h3>{tr(section.title)}</h3>}
                        {section.description && <p>{tr(section.description)}</p>}
                      </div>
                    )}

                    {section.questions &&
                      section.questions.map((q, qIdx) => {
                        const isMandatory = q.is_mandatory === 1 || q.isMandatory || q.mandatory;
                        const isError = !!validationErrors[q.id];
                        return (
                          <div
                            key={q.id || qIdx}
                            id={`q-card-${q.id}`}
                            className={`ps-question-card ${isError ? "ps-question-card--error" : ""}`}
                          >
                            <div className="ps-q-header">
                              <span className="ps-q-text">{tr(q.question_text || q.text || q.title || q.name || q.question)}</span>
                              {isMandatory && (
                                <span className="ps-mandatory-badge">* {tr(STATIC_LABELS.mandatory)}</span>
                              )}
                            </div>

                            {(q.description || q.desc) && (
                              <p className="ps-q-desc">{tr(q.description || q.desc)}</p>
                            )}

                            {(q.mediaUrl || q.media_url || q.pic) && (
                              <div className="ps-q-media">
                                {(q.mediaType?.startsWith("video/") || (q.media_url || q.pic)?.match(/\.(mp4|webm|ogg)$/i)) ? (
                                  <video src={q.mediaUrl || q.media_url || q.pic} controls />
                                ) : (
                                  <img src={q.mediaUrl || q.media_url || q.pic} alt="Question Media" />
                                )}
                              </div>
                            )}

                            <div className="ps-options-list">
                              {getOptionsForQuestion(q).map((opt) => {
                                const isSelected = answers[q.id] === opt;
                                return (
                                  <div
                                    key={opt}
                                    className={`ps-option-pill ${isSelected ? "ps-option-pill--selected" : ""}`}
                                    onClick={() => handleSelectOption(q.id, opt)}
                                  >
                                    <span>{tr(opt)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ))}
            </div>
          ))}

        <div className="ps-submit-container">
          <button
            type="submit"
            className="ps-btn-submit"
            disabled={submitting}
          >
            {submitting ? tr(STATIC_LABELS.submitting) : tr(STATIC_LABELS.submitSurvey)}
          </button>
        </div>
      </form>
    </div>
  );
}